import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Minus } from "lucide-react";
import { getProduct } from "@/data/products";
import Seo from "@/components/site/Seo";
import ComingSoon from "@/components/site/ComingSoon";
import { EASE, Eyebrow, Reveal } from "@/components/site/motion";

function ListSection({ title, items, icon = "check", testId }) {
  if (!items || items.length === 0) return null;
  return (
    <Reveal className="border-t border-slate-200 py-10" data-testid={testId}>
      <h2 className="text-xl font-bold tracking-tight text-slate-900">{title}</h2>
      <ul className="mt-6 grid gap-x-10 gap-y-3 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
            {icon === "check" ? (
              <Check size={15} className="mt-0.5 shrink-0 text-blue-600" strokeWidth={2.5} />
            ) : (
              <Minus size={15} className="mt-0.5 shrink-0 text-slate-300" strokeWidth={2.5} />
            )}
            {item}
          </li>
        ))}
      </ul>
    </Reveal>
  );
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const p = getProduct(slug);

  if (!p) return <ComingSoon />;

  return (
    <main className="bg-white" data-testid="product-detail-page">
      <Seo title={`${p.name} | Bytevora`} description={p.cardDesc} path={`/products/${p.slug}`} />

      {/* Header */}
      <section className="relative overflow-hidden border-b border-slate-100 pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 top-[-140px] h-[400px] w-[400px] rounded-full bg-blue-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-[1200px] px-6 py-14 md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Link
              to="/products"
              data-testid="product-back-link"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-blue-600"
            >
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
              Products / {p.name}
            </Link>
          </motion.div>
          <div className="mt-8 grid items-start gap-10 lg:grid-cols-[1.25fr_0.75fr]">
            <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, ease: EASE, delay: 0.1 }}>
              <Eyebrow>{p.eyebrow}</Eyebrow>
              <h1 data-testid="product-title" className="mt-5 max-w-[640px] text-[clamp(2.1rem,4.4vw,3.6rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-slate-900">
                {p.h1}
              </h1>
              <p className="mt-5 max-w-[560px] text-base leading-relaxed text-slate-600 md:text-lg">{p.sub}</p>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400" data-testid="product-meta">
                {p.meta}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: EASE, delay: 0.22 }}
              className="rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_30px_60px_-45px_rgba(11,18,32,0.3)] lg:sticky lg:top-28"
              data-testid="product-purchase-card"
            >
              <div className="flex items-center justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-blue-600">
                  <p.icon size={19} strokeWidth={1.7} />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{p.category}</span>
              </div>
              <p className="mt-6 text-[34px] font-bold tracking-tight text-slate-900" data-testid="product-price">{p.priceLabel}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{p.delivery} · {p.deliveryType}</p>
              <p className="mt-1 text-xs text-slate-400">Applicable taxes, if any, will be communicated before payment.</p>
              <Link
                to={`/checkout/${p.slug}`}
                data-testid="product-buy-button"
                className="group mt-6 flex w-full items-center justify-center gap-2.5 rounded-[10px] bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
              >
                {p.ctaLabel}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              {p.secondaryCta && (
                <Link
                  to={p.secondaryCta.to}
                  data-testid="product-secondary-cta"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-[10px] border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors duration-300 hover:border-slate-400"
                >
                  {p.secondaryCta.label}
                </Link>
              )}
              <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
                Purchases are covered by our{" "}
                <Link to="/refund-cancellation-policy" className="text-blue-600">Refund &amp; Cancellation Policy</Link> and{" "}
                <Link to="/shipping-delivery-policy" className="text-blue-600">Shipping &amp; Delivery Policy</Link>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto w-full max-w-[1200px] px-6 py-4 md:px-8">
        <Reveal className="py-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Overview</h2>
          {p.overview.map((para, i) => (
            <p key={i} className="mt-4 max-w-3xl text-[15px] leading-[1.8] text-slate-600">
              {para}
            </p>
          ))}
        </Reveal>

        <ListSection title="What's Included" items={p.included} testId="product-included" />
        <ListSection title="Topics We Can Cover" items={p.topics} testId="product-topics" />
        <ListSection title="What You'll Receive" items={p.receive} testId="product-receive" />
        <ListSection title="What We Need From You" items={p.need} testId="product-need" />
        <ListSection title="Optional Add-ons (Quoted Separately)" items={p.addons} testId="product-addons" />
        <ListSection title="What's Not Included" items={p.notIncluded} icon="minus" testId="product-not-included" />

        <Reveal className="border-t border-slate-200 py-10" data-testid="product-delivery">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Delivery</h2>
          <p className="mt-4 max-w-3xl text-[15px] leading-[1.8] text-slate-600">{p.deliveryNote}</p>
          {p.scopeNote && (
            <p className="mt-5 max-w-3xl border-l-2 border-blue-600 pl-5 text-sm font-medium leading-relaxed text-slate-800">
              {p.scopeNote}
            </p>
          )}
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-500">
            Because work may begin after your order is accepted, refund eligibility is governed by
            our{" "}
            <Link to="/refund-cancellation-policy" className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-4 hover:decoration-blue-600">
              Refund &amp; Cancellation Policy
            </Link>
            .
          </p>
        </Reveal>

        {/* Final CTA */}
        <Reveal className="border-t border-slate-200 py-14">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Ready to get started?</h2>
              <p className="mt-2.5 text-sm text-slate-500 md:text-base">
                {p.name} — {p.priceLabel} · {p.delivery}
              </p>
            </div>
            <Link
              to={`/checkout/${p.slug}`}
              data-testid="product-bottom-buy-button"
              className="group inline-flex shrink-0 items-center gap-2.5 rounded-[10px] bg-navy px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
            >
              {p.ctaLabel}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>

      {/* Mobile sticky purchase bar */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 px-5 py-3.5 backdrop-blur-xl lg:hidden" data-testid="product-sticky-bar">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-lg font-bold tracking-tight text-slate-900">{p.priceLabel}</p>
            <p className="text-[11px] text-slate-500">{p.delivery}</p>
          </div>
          <Link
            to={`/checkout/${p.slug}`}
            data-testid="product-sticky-buy-button"
            className="inline-flex items-center gap-2 rounded-[10px] bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
          >
            Buy Now
            <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </main>
  );
}
